import { useEffect, useEffectEvent, useRef, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/react';
import { DragDropProvider, DragOverlay } from '@dnd-kit/react';
import { isSortable } from '@dnd-kit/react/sortable';
import {
  IconAlertTriangle,
  IconEdit,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAmplitudeTrack } from 'amplitude/useAmplitude';
import { useAtomValue } from 'jotai';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { AmplitudeProductEvent } from '@cfa/pathway-fe-shared';
import {
  deleteQuiz as deleteQuizRequest,
  getQuizDetailsOptions,
  getQuizDetailsQueryKey,
  getQuizzesQueryKey,
  patchQuizNameMutation,
  type QuizDetail,
} from '@cfa/pathway-fe-shared/xp';
import { Button, Card, MessageBlock, Select } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';
import usePlanOwner from '@/hooks/usePlanOwner';

import { cn } from '@/utils/tailwind';
import { validateTextField } from '@/utils/validateTextField';

import StickyMenuButton from '@/components/Cards/StickyMenuCard/StickyMenuButton';
import StickyMenuCard from '@/components/Cards/StickyMenuCard/StickyMenuCard';
import { Header } from '@/components/header';

import { queryClient } from '@/services/api';

import GenericError from 'components/Error/GenericError';
import LoadingOverlay from 'components/LoadingOverlay/LoadingOverlay';
import PopoverMenuButton from 'components/PopoverMenuButton/PopoverMenuButton';
import PopoverMenuButtonItem from 'components/PopoverMenuButton/PopoverMenuButtonItem';
import ConfirmationModal from 'components/popups/ConfirmationModal';
import { PathwayModal } from 'components/popups/PathwayModal';
import ToastMessageBlock from 'components/Toasts/SuccessToast';
import { routes } from 'constants/index';
import { itemByLanguage } from 'utils/language';

import CollapsedQuestionPreview from './BuildQuizzesItems/CollapsedQuestionPreview';
import DeleteQuizPopup from './BuildQuizzesItems/DeleteQuizPopup';
import RenameQuizPopup from './BuildQuizzesItems/RenameQuizPopup';
import SortableQuestionCardV2 from './BuildQuizzesItems/SortableQuestionCardV2';
import TranslatingCheck from './BuildQuizzesItems/TranslatingCheck';
import ViewQuizTranslationsModal from './BuildQuizzesItems/ViewQuizTranslationsModal';
import {
  createDefaultV2QuizEditorState,
  createEmptyMultipleChoiceQuestion,
  oppositeLanguage,
  patchAnswerText,
  patchQuestionPrompt,
  quizDetailToV2EditorState,
  type QuizLocale,
  type V2QuizEditorState,
} from './state';
import {
  applyAllTranslations,
  buildTranslatedQuizName,
  hasUntranslatedText,
  translateText,
} from './translateText';
import { useV2QuizSave } from './useV2QuizSave';
import {
  hasIncompletePrimaryQuestionWork,
  hasIncompleteQuestionWork,
  isQuestionPrimaryComplete,
  isTitleComplete,
} from './v2QuizValidation';

import { activeLicenseeLocationAtom } from '@/atoms/licensee-location';
import { userPrefsAtom } from '@/atoms/userPrefs';

// Drag-and-drop animation timing (ported from BuildQuizzesView).
//
//   SCROLL_TRIGGER_DELAY_MS delay after drop before the two-phase scroll.
//                           Must be >= the card expand duration (300 ms).
//   DROP_HIGHLIGHT_FADE_MS  blue drop-target fade — must match Card transition.
//   SCROLL_END_FALLBACK_MS  safety timeout if scrollend never fires.
const SCROLL_TRIGGER_DELAY_MS = 450;
const DROP_HIGHLIGHT_FADE_MS = 750;
const SCROLL_END_FALLBACK_MS = 1200;

/** Non-draft regions while a question is in progress (siblings only — not the active card). */
const draftSiblingTransitionClassName =
  'transition-[filter,opacity] duration-500 ease-out';

const draftSiblingDimActiveClassName =
  'pointer-events-none opacity-50 blur-[4px]';

interface BuildQuizzesLocationState {
  ownerId?: string;
  quizName?: string;
  buildStartedAt?: number;
}

const CREATE_ROUTE_QUIZ_ID = 'new';
const SAVING_QUIZ_MIN_MS = 1000;
const MISSING_TRANSLATIONS_KEY = 'missing-translations';

/** Fill `current`'s empty translation only if its source text is what was sent. */
const fillTranslation = <T extends { en?: string; es?: string }>(
  current: T,
  sent: T,
  translated: T,
  source: QuizLocale,
): T => {
  const other = oppositeLanguage(source);
  const value = translated[other];
  if (!value || current[other]?.trim() || current[source] !== sent[source]) {
    return current;
  }
  return { ...current, [other]: value };
};

/** Apply a missing-translations response without clobbering edits made meanwhile. */
const mergeMissingTranslations = (
  current: V2QuizEditorState,
  sent: V2QuizEditorState,
  translated: V2QuizEditorState,
  source: QuizLocale,
): V2QuizEditorState => ({
  ...current,
  name: fillTranslation(current.name, sent.name, translated.name, source),
  questions: current.questions.map(question => {
    const sentIndex = sent.questions.findIndex(
      item => item.clientId === question.clientId,
    );
    const sentQuestion = sent.questions[sentIndex];
    const translatedQuestion = translated.questions[sentIndex];
    if (!sentQuestion || !translatedQuestion) {
      return question;
    }
    return {
      ...question,
      question: fillTranslation(
        question.question,
        sentQuestion.question,
        translatedQuestion.question,
        source,
      ),
      answers: question.answers.map((answer, answerIndex) => {
        const sentAnswer = sentQuestion.answers[answerIndex];
        const translatedAnswer = translatedQuestion.answers[answerIndex];
        if (!sentAnswer || !translatedAnswer) {
          return answer;
        }
        return {
          ...answer,
          answer: fillTranslation(
            answer.answer,
            sentAnswer.answer,
            translatedAnswer.answer,
            source,
          ),
        };
      }),
    };
  }),
});

const isQuizEditorFieldFocused = () => {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) {
    return false;
  }
  return active.closest('[data-testid="BuildQuizzesViewV2"]') != null;
};

const BuildQuizzesViewV2 = () => {
  const { t } = useTranslation();
  const { data: user } = useAuth();
  const { resolvePlanOwner } = usePlanOwner({
    isEligible: location => location.permissions.canManageQuizzes,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { quizId: routeQuizId } = useParams<{ quizId: string }>();
  const locationState = location.state as BuildQuizzesLocationState | null;
  const track = useAmplitudeTrack();
  const activeLicenseeLocation = useAtomValue(activeLicenseeLocationAtom);
  const { language: prefsLanguage } = useAtomValue(userPrefsAtom);
  const userLanguage: QuizLocale = prefsLanguage === 'es' ? 'es' : 'en';
  const isLicenseeUser = user?.isLicensee;

  const isCreateRoute = !routeQuizId || routeQuizId === CREATE_ROUTE_QUIZ_ID;

  const [quiz, setQuiz] = useState(createDefaultV2QuizEditorState);
  const [isSeeded, setIsSeeded] = useState(false);
  /** Bumps on every translate start so the Lottie restarts. */
  const [translatePlayToken, setTranslatePlayToken] = useState(0);
  const [showTranslations, setShowTranslations] = useState(false);
  const [showConfirmLeave, setShowConfirmLeave] = useState(false);
  const [showSavingQuiz, setShowSavingQuiz] = useState(false);
  const [showRenameQuizPopup, setShowRenameQuizPopup] = useState(false);
  const [showDeleteQuizPopup, setShowDeleteQuizPopup] = useState(false);
  const [validationMode, setValidationMode] = useState(false);
  const [
    isPrimaryLanguageBannerDismissed,
    setIsPrimaryLanguageBannerDismissed,
  ] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [collapsedCardId, setCollapsedCardId] = useState<string | null>(null);
  const [droppedCardId, setDroppedCardId] = useState<string | null>(null);
  /** Autosave is paused because a translation could not be fetched. */
  const [hasTranslationFailure, setHasTranslationFailure] = useState(false);
  /** Keeps grip gate reactive when translate map size changes. */
  const [pendingTranslateCount, setPendingTranslateCount] = useState(0);
  const [draftClientId, setDraftClientId] = useState<string | null>(null);
  const draftFocusClientIdRef = useRef<string | null>(null);
  const shouldFocusAfterDiscardRef = useRef(false);
  const buildStartedAtRef = useRef<number | null>(null);
  const translateControllersRef = useRef(new Map<string, AbortController>());
  const quizRef = useRef(quiz);
  useEffect(() => {
    quizRef.current = quiz;
  }, [quiz]);
  const allowLeaveRef = useRef(false);
  const isFlushingLeaveRef = useRef(false);
  /** Where Header/back/Navbar leave should navigate after flush or discard. */
  const pendingLeavePathRef = useRef(routes.plans.managedQuizzes as string);
  const dragTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const scrollEndListenerRef = useRef<(() => void) | null>(null);
  const dragSourceOriginalTopRef = useRef<number | null>(null);

  useEffect(() => {
    const cleanup = () => {
      dragTimeoutsRef.current.forEach(clearTimeout);
      if (scrollEndListenerRef.current) {
        window.removeEventListener('scrollend', scrollEndListenerRef.current);
      }
    };
    return cleanup;
  }, []);

  const {
    data: quizData,
    isPending: isPendingQuiz,
    isError: isQuizError,
    isFetching: isFetchingQuiz,
  } = useQuery({
    ...getQuizDetailsOptions({
      path: {
        quizId: routeQuizId ?? '',
      },
    }),
    enabled: !isCreateRoute,
  });

  const registerTranslate = (key: string, abort: AbortController) => {
    const previous = translateControllersRef.current.get(key);
    previous?.abort();
    translateControllersRef.current.set(key, abort);
    setPendingTranslateCount(translateControllersRef.current.size);
    setTranslatePlayToken(token => token + 1);
  };

  const clearTranslate = (key: string, abort: AbortController) => {
    // Ignore clears from superseded requests for the same key.
    if (translateControllersRef.current.get(key) !== abort) {
      return;
    }
    translateControllersRef.current.delete(key);
    setPendingTranslateCount(translateControllersRef.current.size);
  };

  const hasPendingTranslates = () => translateControllersRef.current.size > 0;

  const handleCreated = (newQuizId: string) => {
    track(AmplitudeProductEvent.QUIZ_CREATED, {
      quiz_id: newQuizId,
      ...(buildStartedAtRef.current != null
        ? {
            duration_ms: Date.now() - buildStartedAtRef.current,
          }
        : {}),
    });
    // Same route (`:quizId`) — param change only, no remount.
    navigate(routes.plans.managedQuiz({ quizId: newQuizId }), {
      replace: true,
      state: locationState,
    });
  };

  const {
    scheduleSaveFromState,
    hasInflightOrQueuedSave,
    waitUntilIdle,
    didLastSaveFail,
    savingQuestionClientId,
  } = useV2QuizSave({
    setQuiz,
    onCreated: handleCreated,
    locationNumber: isLicenseeUser ? activeLicenseeLocation?.number : undefined,
  });

  const renameQuiz = useMutation({
    ...patchQuizNameMutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getQuizzesQueryKey(),
        refetchType: 'all',
      });
    },
    onError: error => {
      datadogRum.addError(error);
    },
  });

  const seedCreateFromNavigation = useEffectEvent(async () => {
    const quizName = locationState?.quizName?.trim();
    const { ownerId } = resolvePlanOwner(locationState?.ownerId);

    if (!quizName || !ownerId) {
      if (!ownerId) {
        datadogRum.addError(
          new Error('Missing owner id when creating V2 quiz'),
        );
      }
      navigate(routes.plans.managedQuizzes);
      return;
    }

    const other = oppositeLanguage(userLanguage);

    buildStartedAtRef.current = locationState?.buildStartedAt ?? Date.now();

    setQuiz({
      quizId: '',
      ownerId,
      primaryLanguage: userLanguage,
      name: { [userLanguage]: quizName },
      passingGrade: 0,
      questions: [createEmptyMultipleChoiceQuestion()],
      savingQuestionIndex: null,
      isSaving: false,
    });
    setIsSeeded(true);

    const titleTranslateKey = 'quiz-title';
    const abort = new AbortController();
    registerTranslate(titleTranslateKey, abort);
    try {
      const translated = await translateText(quizName, userLanguage).catch(
        () => null,
      );
      if (abort.signal.aborted) {
        return;
      }
      const next = translated
        ? {
            ...quizRef.current,
            name: { ...quizRef.current.name, [other]: translated },
          }
        : quizRef.current;
      quizRef.current = next;
      setQuiz(next);
      // Retries dropped field saves; a failed title goes to the missing-translations retry.
      clearTranslate(titleTranslateKey, abort);
      trySave(next, null);
    } finally {
      clearTranslate(titleTranslateKey, abort);
    }
  });

  const hasLiveEditorSession = () => isSeeded && quizRef.current.quizId !== '';

  const hydrateFromFetchedQuiz = useEffectEvent(() => {
    if (!quizData) {
      if (!isFetchingQuiz && !hasLiveEditorSession()) {
        navigate(routes.plans.managedQuizzes);
      }
      return;
    }

    if (!quizData.v2) {
      if (!(isSeeded && quiz.quizId === quizData.quizId)) {
        navigate(routes.plans.managedQuizzes);
      }
      return;
    }

    // Create session already has this quiz in memory after POST — don't clobber.
    if (isSeeded && quiz.quizId === quizData.quizId) {
      return;
    }

    setQuiz(quizDetailToV2EditorState(quizData));
    setIsSeeded(true);
  });

  // Seeding before Settings language loads would lock primaryLanguage to 'en'.
  const hasPrefsLanguage = prefsLanguage !== '';

  useEffect(() => {
    if (!isCreateRoute || !hasPrefsLanguage) {
      return;
    }

    const controllers = translateControllersRef.current;
    void seedCreateFromNavigation();
    return () => {
      // URL swap from /new to the created id stays mounted. Abort only the
      // seed title request, not in-flight field translations.
      const title = controllers.get('quiz-title');
      title?.abort();
      if (title) {
        controllers.delete('quiz-title');
      }
    };
  }, [isCreateRoute, hasPrefsLanguage]);

  useEffect(() => {
    const controllers = translateControllersRef.current;
    return () => {
      controllers.forEach(controller => controller.abort());
      controllers.clear();
      setPendingTranslateCount(0);
    };
  }, []);

  useEffect(() => {
    if (isCreateRoute) {
      return;
    }
    hydrateFromFetchedQuiz();
  }, [isCreateRoute, quizData, isFetchingQuiz]);

  const trySave = (
    state: V2QuizEditorState,
    questionIndex: number | null,
    { retryMissingTranslations = true } = {},
  ) => {
    if (hasPendingTranslates()) {
      return;
    }
    if (hasUntranslatedText(state, userLanguage)) {
      if (retryMissingTranslations) {
        void translateMissingThenSave(questionIndex);
      } else {
        setHasTranslationFailure(true);
      }
      return;
    }
    setHasTranslationFailure(false);
    if (!isTitleComplete(state.name, state.primaryLanguage)) {
      return;
    }
    scheduleSaveFromState(state, questionIndex);
  };

  /** Retry translations a failed per-field request left empty, then save. */
  const translateMissingThenSave = async (questionIndex: number | null) => {
    const abort = new AbortController();
    registerTranslate(MISSING_TRANSLATIONS_KEY, abort);
    const snapshot = quizRef.current;
    try {
      const result = await applyAllTranslations(snapshot, userLanguage, {
        onlyMissing: true,
      });
      if (abort.signal.aborted) {
        return;
      }
      if (!result.ok) {
        setHasTranslationFailure(true);
        return;
      }
      const next = mergeMissingTranslations(
        quizRef.current,
        snapshot,
        result.state,
        userLanguage,
      );
      quizRef.current = next;
      setQuiz(next);
      clearTranslate(MISSING_TRANSLATIONS_KEY, abort);
      trySave(next, questionIndex, { retryMissingTranslations: false });
    } finally {
      clearTranslate(MISSING_TRANSLATIONS_KEY, abort);
    }
  };

  const retryTranslations = () => {
    setHasTranslationFailure(false);
    trySave(quizRef.current, null);
  };

  const addQuestion = () => {
    const newQuestion = createEmptyMultipleChoiceQuestion();
    draftFocusClientIdRef.current = newQuestion.clientId;
    setDraftClientId(newQuestion.clientId);
    setQuiz(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion],
    }));
  };

  const discardDraftQuestion = () => {
    if (!draftClientId) {
      return;
    }
    abortTranslatesForClientId(draftClientId);
    const questions = quizRef.current.questions.filter(
      item => item.clientId !== draftClientId,
    );
    const next = {
      ...quizRef.current,
      questions,
      passingGrade: Math.min(quizRef.current.passingGrade, questions.length),
    };
    quizRef.current = next;
    setQuiz(next);
    shouldFocusAfterDiscardRef.current = true;
    setDraftClientId(null);
  };

  const handlePassingGrade = (value: number) => {
    const nextForSave = { ...quizRef.current, passingGrade: value };
    quizRef.current = nextForSave;
    setQuiz(nextForSave);
    if (nextForSave.quizId) {
      trySave(nextForSave, null);
    }
  };

  const needsSaveFlush = () =>
    hasPendingTranslates() ||
    hasInflightOrQueuedSave() ||
    isQuizEditorFieldFocused() ||
    !isTitleComplete(quizRef.current.name, quizRef.current.primaryLanguage);

  const leaveEditor = (to: string = pendingLeavePathRef.current) => {
    allowLeaveRef.current = true;
    pendingLeavePathRef.current = routes.plans.managedQuizzes;

    const quizId = quizRef.current.quizId;
    void queryClient.invalidateQueries({
      queryKey: getQuizzesQueryKey(),
      refetchType: 'all',
    });
    if (quizId) {
      // staleTime: Infinity + refetchOnMount: false — drop details so re-open refetches.
      queryClient.removeQueries({
        queryKey: getQuizDetailsQueryKey({
          path: { quizId },
        }),
      });
    }

    navigate(to);
  };

  /** Pull the focused input's DOM value into editor state (back skips React blur). */
  const syncFocusedFieldIntoState = () => {
    const active = document.activeElement;
    if (
      !(active instanceof HTMLInputElement) &&
      !(active instanceof HTMLTextAreaElement)
    ) {
      return;
    }

    const card = active.closest('[data-testid^="QuestionCardV2-"]');
    const cardMatch = card
      ?.getAttribute('data-testid')
      ?.match(/^QuestionCardV2-(.+)$/);
    if (!cardMatch) {
      return;
    }

    const clientId = cardMatch[1];
    const questionIndex = quizRef.current.questions.findIndex(
      q => q.clientId === clientId,
    );
    if (questionIndex < 0) {
      return;
    }
    const fieldTestId =
      active.closest('[data-testid]')?.getAttribute('data-testid') ?? '';
    const value = active.value;

    if (fieldTestId === 'EnterQuestion') {
      setQuiz(prev => {
        const next = patchQuestionPrompt(prev, questionIndex, {
          [userLanguage]: value,
        });
        quizRef.current = next;
        return next;
      });
      return;
    }

    const answerMatch = /^EnterAnswer(\d+)$/.exec(fieldTestId);
    if (!answerMatch) {
      return;
    }
    const optionIndex = Number(answerMatch[1]) - 1;
    setQuiz(prev => {
      const next = patchAnswerText(prev, questionIndex, optionIndex, {
        [userLanguage]: value,
      });
      quizRef.current = next;
      return next;
    });
  };

  const abortPendingTranslates = () => {
    translateControllersRef.current.forEach(controller => controller.abort());
    translateControllersRef.current.clear();
    setPendingTranslateCount(0);
  };

  const abortTranslatesForClientId = (clientId: string) => {
    const promptKey = `prompt:${clientId}`;
    const answerPrefix = `answer:${clientId}:`;
    const keysToAbort: string[] = [];
    translateControllersRef.current.forEach((_, key) => {
      if (key === promptKey || key.startsWith(answerPrefix)) {
        keysToAbort.push(key);
      }
    });
    for (const key of keysToAbort) {
      translateControllersRef.current.get(key)?.abort();
      translateControllersRef.current.delete(key);
    }
    setPendingTranslateCount(translateControllersRef.current.size);
  };

  const clearDragTimers = () => {
    dragTimeoutsRef.current.forEach(clearTimeout);
    dragTimeoutsRef.current = [];
    if (scrollEndListenerRef.current) {
      window.removeEventListener('scrollend', scrollEndListenerRef.current);
      scrollEndListenerRef.current = null;
    }
  };

  const applyReorderedQuestions = (
    questions: V2QuizEditorState['questions'],
  ) => {
    const next = { ...quizRef.current, questions };
    quizRef.current = next;
    setQuiz(next);
    trySave(next, null);
  };

  const onDragStart = (event: DragStartEvent) => {
    const { source } = event.operation;
    if (!isSortable(source)) return;
    clearDragTimers();
    const cardId = quizRef.current.questions[source.index]?.clientId ?? null;
    setDroppedCardId(cardId);
    setCollapsedCardId(null);
    setIsDragging(true);
    setDraggingIndex(source.index);
    const el = cardId
      ? document.querySelector(`[data-question-id="${cardId}"]`)
      : null;
    dragSourceOriginalTopRef.current = el
      ? el.getBoundingClientRect().top + window.scrollY - 100
      : null;
  };

  const onDragEnd = (event: DragEndEvent) => {
    setIsDragging(false);
    setDraggingIndex(null);
    clearDragTimers();
    if (event.canceled) {
      setDroppedCardId(null);
      return;
    }
    const { source } = event.operation;
    if (!isSortable(source)) {
      setDroppedCardId(null);
      return;
    }
    const { initialIndex, index } = source;
    if (initialIndex === index) {
      setDroppedCardId(null);
      return;
    }

    const questions = [...quizRef.current.questions];
    const [removed] = questions.splice(initialIndex, 1);
    if (removed === undefined) {
      setDroppedCardId(null);
      return;
    }
    questions.splice(index, 0, removed);
    const removedId = removed.clientId;
    setCollapsedCardId(removedId);
    applyReorderedQuestions(questions);

    const originalScrollTarget = dragSourceOriginalTopRef.current;
    dragSourceOriginalTopRef.current = null;
    dragTimeoutsRef.current.push(
      setTimeout(() => {
        const expand = () => {
          setCollapsedCardId(null);
          dragTimeoutsRef.current.push(
            setTimeout(() => setDroppedCardId(null), DROP_HIGHLIGHT_FADE_MS),
          );
        };
        const scrollToNewThenExpand = () => {
          const el = document.querySelector(
            `[data-question-id="${removedId}"]`,
          );
          if (el) {
            const newTop =
              el.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top: newTop, behavior: 'smooth' });
          }
          const fallback = setTimeout(() => {
            if (scrollEndListenerRef.current) {
              window.removeEventListener(
                'scrollend',
                scrollEndListenerRef.current,
              );
              scrollEndListenerRef.current = null;
            }
            expand();
          }, SCROLL_END_FALLBACK_MS);
          dragTimeoutsRef.current.push(fallback);
          const onScrollEnd = () => {
            clearTimeout(fallback);
            scrollEndListenerRef.current = null;
            expand();
          };
          scrollEndListenerRef.current = onScrollEnd;
          window.addEventListener('scrollend', onScrollEnd, { once: true });
        };
        if (originalScrollTarget !== null) {
          window.scrollTo({ top: originalScrollTarget, behavior: 'instant' });
        }
        scrollToNewThenExpand();
      }, SCROLL_TRIGGER_DELAY_MS),
    );
  };

  const moveQuestionUp = (questionIndex: number) => {
    if (
      questionIndex <= 0 ||
      questionIndex >= quizRef.current.questions.length
    ) {
      return;
    }
    const newQuestions = [...quizRef.current.questions];
    const prevQuestion = newQuestions[questionIndex - 1];
    const currentQuestion = newQuestions[questionIndex];
    if (prevQuestion === undefined || currentQuestion === undefined) {
      return;
    }
    newQuestions[questionIndex - 1] = currentQuestion;
    newQuestions[questionIndex] = prevQuestion;
    applyReorderedQuestions(newQuestions);
  };

  const moveQuestionDown = (questionIndex: number) => {
    if (
      questionIndex < 0 ||
      questionIndex >= quizRef.current.questions.length - 1
    ) {
      return;
    }
    const newQuestions = [...quizRef.current.questions];
    const currentQuestion = newQuestions[questionIndex];
    const nextQuestion = newQuestions[questionIndex + 1];
    if (currentQuestion === undefined || nextQuestion === undefined) {
      return;
    }
    newQuestions[questionIndex] = nextQuestion;
    newQuestions[questionIndex + 1] = currentQuestion;
    applyReorderedQuestions(newQuestions);
  };

  const flushSaveAndLeave = async () => {
    if (isFlushingLeaveRef.current) {
      return;
    }
    isFlushingLeaveRef.current = true;
    setShowConfirmLeave(false);
    setShowSavingQuiz(true);
    const startedAt = Date.now();
    let shouldLeave = false;
    let flushLeaveFailed = false;

    try {
      // Browser back does not blur; header back may already have blurred with a
      // stale handler. Sync DOM → state, then translate + PUT from that snapshot.
      // We do a full re-translation of all of the questions because we can't be 100% sure
      // that the user has finished typing all of the questions and all of the translations
      // have settled
      syncFocusedFieldIntoState();
      abortPendingTranslates();

      const translation = await applyAllTranslations(
        quizRef.current,
        userLanguage,
      );
      quizRef.current = translation.state;
      setQuiz(translation.state);

      if (!translation.ok) {
        setHasTranslationFailure(true);
        flushLeaveFailed = true;
        return;
      }

      if (!scheduleSaveFromState(translation.state, null)) {
        flushLeaveFailed = true;
        return;
      }

      await waitUntilIdle();
      if (didLastSaveFail()) {
        flushLeaveFailed = true;
        return;
      }

      shouldLeave = true;
    } finally {
      // We wait a little longer so that the user has a chance to see the saving state.
      const remaining = SAVING_QUIZ_MIN_MS - (Date.now() - startedAt);
      if (remaining > 0) {
        await new Promise<void>(resolve => {
          window.setTimeout(resolve, remaining);
        });
      }
      setShowSavingQuiz(false);
      isFlushingLeaveRef.current = false;
      if (shouldLeave) {
        leaveEditor();
      } else if (flushLeaveFailed) {
        if (!didLastSaveFail()) {
          toast.custom(toastObj => (
            <ToastMessageBlock id={toastObj.id} severity="error">
              {t('TrainingPlans.toastMessage.errorUpdating')}
            </ToastMessageBlock>
          ));
        }
        setValidationMode(true);
        setShowConfirmLeave(true);
      }
    }
  };

  const requestLeave = (to: string = routes.plans.managedQuizzes) => {
    if (isFlushingLeaveRef.current) {
      return;
    }
    pendingLeavePathRef.current = to;
    // Back/header can skip React blur — pull DOM into state before gating.
    syncFocusedFieldIntoState();

    // Only primary authoring gaps need discard confirm. Missing translations
    // (e.g. retyped answer, blur not finished) are flushable.
    if (hasIncompletePrimaryQuestionWork(quizRef.current)) {
      setValidationMode(true);
      setShowConfirmLeave(true);
      return;
    }
    if (needsSaveFlush() || hasIncompleteQuestionWork(quizRef.current)) {
      void flushSaveAndLeave();
      return;
    }
    leaveEditor(to);
  };

  const cancelLeave = () => {
    setShowConfirmLeave(false);
    pendingLeavePathRef.current = routes.plans.managedQuizzes;
  };

  const confirmLeave = () => {
    setShowConfirmLeave(false);
    leaveEditor();
  };

  const onBrowserBack = useEffectEvent(() => {
    if (allowLeaveRef.current || isFlushingLeaveRef.current) {
      return;
    }

    // Stay on the editor while we flush or confirm.
    window.history.pushState(null, '', window.location.href);
    pendingLeavePathRef.current = routes.plans.managedQuizzes;

    // Browser back skips React blur — sync before completeness checks.
    syncFocusedFieldIntoState();

    if (hasIncompletePrimaryQuestionWork(quizRef.current)) {
      setValidationMode(true);
      setShowConfirmLeave(true);
      return;
    }

    if (needsSaveFlush() || hasIncompleteQuestionWork(quizRef.current)) {
      void flushSaveAndLeave();
      return;
    }

    leaveEditor(routes.plans.managedQuizzes);
  });

  const onInAppLinkNavigation = useEffectEvent((to: string) => {
    requestLeave(to);
  });

  /**
   * BrowserRouter has no useBlocker — intercept browser back and in-app <a>
   * clicks (Navbar, logo, etc.) so leave still blurs + flushes PUT / confirms.
   */
  useEffect(() => {
    if (!isSeeded) {
      return;
    }

    const onPopState = () => {
      onBrowserBack();
    };

    const onDocumentClickCapture = (event: MouseEvent) => {
      if (allowLeaveRef.current) {
        return;
      }
      if (event.defaultPrevented || event.button !== 0) {
        return;
      }
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const anchor = target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) {
        return;
      }
      if (anchor.target === '_blank' || anchor.hasAttribute('download')) {
        return;
      }

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#')) {
        return;
      }

      let url: URL;
      try {
        url = new URL(href, window.location.origin);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) {
        return;
      }

      const nextPath = `${url.pathname}${url.search}${url.hash}`;
      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (nextPath === currentPath) {
        return;
      }

      // Always stop in-app links while on the editor (including mid-flush).
      event.preventDefault();
      event.stopPropagation();
      if (isFlushingLeaveRef.current) {
        return;
      }
      onInAppLinkNavigation(nextPath);
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', onPopState);
    document.addEventListener('click', onDocumentClickCapture, true);
    return () => {
      window.removeEventListener('popstate', onPopState);
      document.removeEventListener('click', onDocumentClickCapture, true);
    };
  }, [isSeeded, location.pathname]);

  const quizTitle = itemByLanguage(quiz.name, '', userLanguage);
  // CTA + modal preview the opposite of Settings language, not quiz primary.
  const translationLanguage = oppositeLanguage(userLanguage);
  const translationsLabel =
    translationLanguage === 'en'
      ? t('TrainingPlans.buildQuizzes.viewEnglish')
      : t('TrainingPlans.buildQuizzes.viewSpanish');
  /** userPrefs language ≠ immutable quiz.primaryLanguage: view/delete/threshold only. */
  const isDisplayLanguageMismatch = quiz.primaryLanguage !== userLanguage;
  const showPrimaryLanguageBanner =
    !isPrimaryLanguageBannerDismissed && isDisplayLanguageMismatch;

  const onShowRenameQuizPopup = () => {
    if (isDisplayLanguageMismatch) {
      return;
    }
    setShowRenameQuizPopup(true);
  };

  const applyQuizNameToEditor = (bilingualName: V2QuizEditorState['name']) => {
    setQuiz(prev => {
      const next = { ...prev, name: bilingualName };
      quizRef.current = next;
      return next;
    });
  };

  /**
   * Rename while viewing quiz primary language: translate, then PATCH /name.
   * Autosave PUT sends a placeholder title; PATCH /name is the only rename path.
   */
  const onRenameQuizAndClosePopup = async (inputValue: string) => {
    if (isDisplayLanguageMismatch) {
      return false;
    }

    const trimmed = inputValue.trim();
    if (!trimmed) {
      return false;
    }

    try {
      const bilingualName = await buildTranslatedQuizName(
        trimmed,
        userLanguage,
      ).catch(() => null);
      if (!bilingualName) {
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id} severity="error">
            {t('TrainingPlans.toastMessage.errorUpdating')}
          </ToastMessageBlock>
        ));
        return false;
      }

      let quizId = quizRef.current.quizId;
      if (!quizId) {
        await waitUntilIdle();
        quizId = quizRef.current.quizId;
      }

      if (quizId) {
        await renameQuiz.mutateAsync({
          path: { quizId },
          body: { name: bilingualName },
        });
        queryClient.setQueryData<QuizDetail>(
          getQuizDetailsQueryKey({
            path: { quizId },
          }),
          old => (old ? { ...old, name: bilingualName } : old),
        );
        applyQuizNameToEditor(bilingualName);
      } else {
        applyQuizNameToEditor(bilingualName);
        trySave(quizRef.current, null);
      }

      setShowRenameQuizPopup(false);
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {`${trimmed} ${t('TrainingPlans.toastMessage.renamed')}`}
        </ToastMessageBlock>
      ));
      return true;
    } catch {
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="error">
          {t('TrainingPlans.toastMessage.errorUpdating')}
        </ToastMessageBlock>
      ));
      return false;
    }
  };

  /**
   * Unsaved create: leave without saving. Saved: confirm then delete via XP.
   */
  const deleteQuiz = async (id: string, onSuccess?: () => void) => {
    try {
      const { error } = await deleteQuizRequest({ path: { quizId: id } });
      if (error) {
        datadogRum.addError(error);
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id} severity="error">
            {t('TrainingPlans.toastMessage.errorDeleting')}
          </ToastMessageBlock>
        ));
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: getQuizzesQueryKey(),
        refetchType: 'all',
      });
      // Match leaveEditor — drop details so staleTime:Infinity cache cannot resurrect.
      queryClient.removeQueries({
        queryKey: getQuizDetailsQueryKey({
          path: { quizId: id },
        }),
      });
      onSuccess?.();
    } catch (error) {
      datadogRum.addError(error);
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="error">
          {t('TrainingPlans.toastMessage.errorDeleting')}
        </ToastMessageBlock>
      ));
    }
  };

  const onDeleteQuiz = (id: string, quizName: string) => {
    void deleteQuiz(id, () => {
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>{`${quizName} ${t(
          'TrainingPlans.toastMessage.deleted',
        )}`}</ToastMessageBlock>
      ));
      allowLeaveRef.current = true;
      navigate(routes.plans.managedQuizzes);
    });
  };

  /** Unsaved create: let an in-flight POST settle so its quiz is not orphaned. */
  const discardUnsavedQuiz = async () => {
    allowLeaveRef.current = true;
    abortPendingTranslates();
    await waitUntilIdle();
    const createdId = quizRef.current.quizId;
    if (createdId) {
      await deleteQuiz(createdId);
    }
    navigate(routes.plans.managedQuizzes);
  };

  const onDeleteQuizAndClosePopup = () => {
    setShowDeleteQuizPopup(false);
    if (!quizRef.current.quizId) {
      void discardUnsavedQuiz();
      return;
    }
    onDeleteQuiz(
      quizRef.current.quizId,
      itemByLanguage(quizRef.current.name, '', userLanguage),
    );
  };

  const onShowDeleteQuizPopup = () => {
    if (!quizRef.current.quizId) {
      void discardUnsavedQuiz();
      return;
    }
    setShowDeleteQuizPopup(true);
  };

  // Primary-language gate: a focused, un-blurred field has no translation yet,
  // and reorder/delete go through trySave, which fills missing translations first.
  const canReorderQuestions =
    !isDisplayLanguageMismatch &&
    isTitleComplete(quiz.name, quiz.primaryLanguage) &&
    !hasIncompletePrimaryQuestionWork(quiz) &&
    pendingTranslateCount === 0 &&
    !hasInflightOrQueuedSave();

  const draftQuestion =
    draftClientId == null
      ? undefined
      : quiz.questions.find(item => item.clientId === draftClientId);

  if (
    draftClientId != null &&
    (draftQuestion == null ||
      isQuestionPrimaryComplete(draftQuestion, userLanguage))
  ) {
    setDraftClientId(null);
  }

  const isDraftActive =
    draftClientId != null &&
    draftQuestion != null &&
    !isQuestionPrimaryComplete(draftQuestion, userLanguage);

  // For a11y, focus on an existing element once the draft question is discarded
  useEffect(() => {
    const pendingFocusClientId = draftFocusClientIdRef.current;
    if (pendingFocusClientId && draftClientId === pendingFocusClientId) {
      draftFocusClientIdRef.current = null;
      const root = document.querySelector(
        `[data-question-id="${pendingFocusClientId}"]`,
      );
      root
        ?.querySelector<HTMLElement>(
          '[data-testid="EnterQuestion"] input, [data-testid="EnterQuestion"] textarea',
        )
        ?.focus();
      return;
    }
    if (shouldFocusAfterDiscardRef.current && draftClientId == null) {
      shouldFocusAfterDiscardRef.current = false;
      document
        .querySelector<HTMLElement>('[data-testid="AddQuizQuestion"]')
        ?.focus();
    }
  }, [draftClientId, quiz.questions.length]);

  const primaryLanguageBanner = showPrimaryLanguageBanner ? (
    <MessageBlock.Root
      severity="info"
      className="w-full! text-start!"
      data-testid="QuizPrimaryLanguageBanner"
      onDismiss={() => setIsPrimaryLanguageBannerDismissed(true)}
    >
      <MessageBlock.Title>
        {quiz.primaryLanguage === 'es'
          ? t('TrainingPlans.buildQuizzes.spanishQuizBannerHeadline')
          : t('TrainingPlans.buildQuizzes.englishQuizBannerHeadline')}
      </MessageBlock.Title>
      {quiz.primaryLanguage === 'es'
        ? t('TrainingPlans.buildQuizzes.spanishQuizBannerBody')
        : t('TrainingPlans.buildQuizzes.englishQuizBannerBody')}
    </MessageBlock.Root>
  ) : null;

  const translationFailureBanner = hasTranslationFailure ? (
    <MessageBlock.Root
      severity="error"
      className="w-full! text-start!"
      data-testid="QuizTranslationFailedBanner"
    >
      <MessageBlock.Title>
        {t('TrainingPlans.buildQuizzes.translationFailedTitle')}
      </MessageBlock.Title>
      {t('TrainingPlans.buildQuizzes.translationFailedBody')}
      <div className="mt-2">
        <Button
          variant="outlined"
          color="secondary"
          size="sm"
          onPress={retryTranslations}
          isDisabled={pendingTranslateCount > 0}
          data-testid="RetryQuizTranslations"
        >
          {t('TrainingPlans.buildQuizzes.retryTranslation')}
        </Button>
      </div>
    </MessageBlock.Root>
  ) : null;

  const translationStatusIcon = hasTranslationFailure ? (
    <IconAlertTriangle
      size={16}
      className="text-feedback-error-base"
      aria-hidden
    />
  ) : (
    <TranslatingCheck playToken={translatePlayToken} />
  );

  if (!isCreateRoute && isQuizError && !hasLiveEditorSession()) {
    return <GenericError />;
  }

  if (!isCreateRoute && isPendingQuiz && !isSeeded) {
    return <LoadingOverlay isOpen fixed />;
  }

  if (!prefsLanguage) {
    return <LoadingOverlay isOpen fixed />;
  }

  if (!isSeeded) {
    return null;
  }

  return (
    <>
      <Header.Root>
        <Header.Back
          as="button"
          type="button"
          onClick={() => requestLeave()}
          tx="TrainingPlans.tabManageQuizzes"
        />
        <Header.Title tx="Generic.editQuiz" />
        <Header.Action>
          <Button
            variant="outlined"
            color="secondary"
            onPress={() => setShowTranslations(true)}
            data-testid="ViewQuizTranslations"
            aria-label={translationsLabel}
            className="min-h-6! gap-2! border-2! px-2.5! py-0.5!"
          >
            {translationsLabel}
            {translationStatusIcon}
          </Button>
        </Header.Action>
      </Header.Root>

      <div
        className="md:mt-header-height-with-content-spacing centered-content px-4 max-md:mt-6"
        data-testid="BuildQuizzesViewV2"
      >
        {translationFailureBanner && (
          <div className="mb-6!">{translationFailureBanner}</div>
        )}
        {primaryLanguageBanner && (
          <div className="mb-6! md:hidden">{primaryLanguageBanner}</div>
        )}
        <div className="flex items-center justify-between gap-4 md:mb-8">
          <p className="text-variant-h3 truncate text-left">{quizTitle}</p>
          <PopoverMenuButton className="md:hidden">
            <PopoverMenuButtonItem
              dataTestId="ThreeDotMenuQuizPopupRename"
              icon={<IconEdit />}
              isDisabled={isDisplayLanguageMismatch}
              onClick={onShowRenameQuizPopup}
              text={t('TrainingPlans.manageThreeDotMenu.renameQuiz')}
            />
            <PopoverMenuButtonItem
              dataTestId="deleteQuizMenuItem"
              icon={<IconTrash />}
              isDestructive
              onClick={onShowDeleteQuizPopup}
              text={t('TrainingPlans.manageThreeDotMenu.deleteQuiz')}
            />
          </PopoverMenuButton>
        </div>
        <div className="text-left md:hidden">
          <Button
            variant="outlined"
            color="secondary"
            onPress={() => setShowTranslations(true)}
            data-testid="ViewQuizTranslations"
            aria-label={translationsLabel}
            className="my-6! min-h-6! gap-2! border-2! px-2.5! py-0.5!"
          >
            {translationsLabel}
            {translationStatusIcon}
          </Button>
        </div>

        <div className="flex flex-col gap-6 md:flex-row">
          <div className="size-full">
            <div
              className={cn(isDisplayLanguageMismatch && 'cursor-not-allowed')}
            >
              <div
                className={cn(
                  isDisplayLanguageMismatch && 'pointer-events-none',
                )}
              >
                <DragDropProvider
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                >
                  <div>
                    {quiz.questions.map((question, questionIndex) => {
                      const cardId = question.clientId;
                      const isDraftCard =
                        cardId === draftClientId && isDraftActive;
                      const dimSibling = isDraftActive && !isDraftCard;
                      return (
                        <div
                          key={cardId}
                          className={cn(
                            draftSiblingTransitionClassName,
                            dimSibling && draftSiblingDimActiveClassName,
                          )}
                          data-testid={
                            dimSibling ? 'QuizDraftDimmedSibling' : undefined
                          }
                          {...(dimSibling ? { inert: true } : {})}
                        >
                          <SortableQuestionCardV2
                            questionId={cardId}
                            questionIndex={questionIndex}
                            quiz={quiz}
                            setQuiz={setQuiz}
                            userLanguage={userLanguage}
                            isSaving={savingQuestionClientId === cardId}
                            validationMode={validationMode}
                            setValidationMode={setValidationMode}
                            trySave={trySave}
                            registerTranslate={registerTranslate}
                            clearTranslate={clearTranslate}
                            canReorder={canReorderQuestions}
                            isCollapsed={cardId === collapsedCardId}
                            isDropTarget={cardId === droppedCardId}
                            isAnyDragActive={isDragging}
                            moveQuestionUp={moveQuestionUp}
                            moveQuestionDown={moveQuestionDown}
                            isDraft={isDraftCard}
                            onDiscardDraft={
                              isDraftCard ? discardDraftQuestion : undefined
                            }
                            isDisplayLanguageMismatch={
                              isDisplayLanguageMismatch
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                  <DragOverlay>
                    {(() => {
                      const draggedQuestion =
                        draggingIndex !== null
                          ? quiz.questions[draggingIndex]
                          : undefined;
                      const draggedQuestionHasError =
                        validationMode &&
                        draggedQuestion !== undefined &&
                        (!validateTextField(
                          draggedQuestion.question[userLanguage] ?? '',
                        ) ||
                          !draggedQuestion.answers.some(
                            option => option.correct === true,
                          ) ||
                          draggedQuestion.answers.some(
                            a =>
                              !validateTextField(a.answer[userLanguage] ?? ''),
                          ));
                      return draggedQuestion !== undefined &&
                        draggingIndex !== null ? (
                        <CollapsedQuestionPreview
                          question={draggedQuestion}
                          questionIndex={draggingIndex}
                          userLanguage={userLanguage}
                          isHighlighted
                          hasError={draggedQuestionHasError}
                        />
                      ) : null;
                    })()}
                  </DragOverlay>
                </DragDropProvider>

                <div
                  className={cn(
                    'mt-4 mb-6 text-left',
                    draftSiblingTransitionClassName,
                    isDraftActive && draftSiblingDimActiveClassName,
                  )}
                  data-testid={
                    isDraftActive ? 'QuizDraftDimmedSibling' : undefined
                  }
                  {...(isDraftActive ? { inert: true } : {})}
                >
                  <Button
                    className="mb-4! gap-2!"
                    color="secondary"
                    data-testid="AddQuizQuestion"
                    isDisabled={
                      isDisplayLanguageMismatch ||
                      hasIncompletePrimaryQuestionWork(quiz)
                    }
                    onPress={addQuestion}
                    size="lg"
                    variant="outlined"
                  >
                    <IconPlus />
                    {t('Button.addQuestion')}
                  </Button>
                </div>
              </div>
            </div>

            <div
              className={cn(
                draftSiblingTransitionClassName,
                isDraftActive && draftSiblingDimActiveClassName,
              )}
              data-testid={isDraftActive ? 'QuizDraftDimmedSibling' : undefined}
              {...(isDraftActive ? { inert: true } : {})}
            >
              <Card.Root outlined className="shadow-md!">
                <Card.Content>
                  <div className="mt-2 mb-4 max-w-[256px] text-left">
                    <Select.Root
                      data-testid="QuizQuestionDropdown"
                      className="w-full!"
                      isDisabled={isDisplayLanguageMismatch}
                      placeholder={t('Button.select')}
                      selectedKey={quiz.passingGrade.toString()}
                      onSelectionChange={key => {
                        if (key == null) {
                          return;
                        }
                        handlePassingGrade(Number(key));
                      }}
                    >
                      <Select.Label>
                        {t(
                          'TrainingPlans.buildQuizzes.correctQuestionsThreshold',
                        )}
                      </Select.Label>
                      <Select.Button>
                        <Select.Value />
                      </Select.Button>
                      <Select.Popover>
                        <Select.List>
                          {[
                            '0',
                            ...quiz.questions.map((_, index) =>
                              (index + 1).toString(),
                            ),
                          ].map(option => (
                            <Select.Item key={option} id={option}>
                              {option}
                            </Select.Item>
                          ))}
                        </Select.List>
                      </Select.Popover>
                    </Select.Root>
                  </div>
                </Card.Content>
              </Card.Root>
            </div>
          </div>

          <div
            className={cn(
              'sticky top-18 hidden w-[270px] min-w-[270px] shrink flex-col gap-4 self-start md:flex',
              draftSiblingTransitionClassName,
              isDraftActive && draftSiblingDimActiveClassName,
            )}
            data-testid={isDraftActive ? 'QuizDraftDimmedSibling' : undefined}
            {...(isDraftActive ? { inert: true } : {})}
          >
            <StickyMenuCard
              header={t('TrainingPlans.buildQuizzes.manageQuiz')}
              className="text-left! md:top-[calc(var(--header-height)+16px)]!"
            >
              <StickyMenuButton
                dataTestId="ThreeDotMenuQuizPopupRename"
                disabled={isDisplayLanguageMismatch}
                icon={<IconEdit />}
                onClick={onShowRenameQuizPopup}
                text={t('TrainingPlans.manageThreeDotMenu.renameQuiz')}
              />
              <StickyMenuButton
                dataTestId="deleteQuizMenuItem"
                error
                icon={<IconTrash />}
                onClick={onShowDeleteQuizPopup}
                text={t('TrainingPlans.manageThreeDotMenu.deleteQuiz')}
              />
            </StickyMenuCard>
            {primaryLanguageBanner}
          </div>
        </div>
      </div>

      <ViewQuizTranslationsModal
        isOpen={showTranslations}
        onClose={() => setShowTranslations(false)}
        quiz={quiz}
        viewLanguage={translationLanguage}
      />

      <RenameQuizPopup
        showRenameQuizPopup={showRenameQuizPopup}
        setShowRenameQuizPopup={setShowRenameQuizPopup}
        onRenameQuizAndClosePopup={onRenameQuizAndClosePopup}
        quizDetails={{ name: quiz.name }}
      />

      <DeleteQuizPopup
        onDeleteQuizAndClosePopup={onDeleteQuizAndClosePopup}
        quizName={quizTitle}
        setShowDeleteQuizPopup={setShowDeleteQuizPopup}
        showDeleteQuizPopup={showDeleteQuizPopup}
      />

      <ConfirmationModal
        bodyText={t('Generic.cancelConfirmation')}
        headerText={t('Generic.unsavedChanges')}
        isOpen={showConfirmLeave}
        onClose={cancelLeave}
        primaryButtonHandler={confirmLeave}
        primaryButtonText={t('Button.discardChanges')}
        primaryButtonVariant="destructive"
        secondaryButtonHandler={cancelLeave}
        secondaryButtonText={t('Button.continueEditing')}
      />

      <PathwayModal
        isOpen={showSavingQuiz}
        onClose={() => undefined}
        showCloseButton={false}
        className="2xl:max-w-[600px]!"
      >
        <PathwayModal.Header>
          <PathwayModal.Title>
            {t('TrainingPlans.buildQuizzes.savingQuiz')}
          </PathwayModal.Title>
        </PathwayModal.Header>
        <PathwayModal.Body noMinHeight>
          <p className="text-grayscale-gray7">
            {t('TrainingPlans.buildQuizzes.savingQuizBody')}
          </p>
        </PathwayModal.Body>
      </PathwayModal>
    </>
  );
};

export default BuildQuizzesViewV2;
